import { Check, MoreHorizontal } from 'lucide-react';
import { type ReactNode, type KeyboardEvent, type RefObject, useEffect, useId, useRef, useState } from 'react';

/**
 * "⋯" menu at the top-right of a material card (owner feedback 2026-10-08): management actions
 * leave the card face. Menu button pattern: Enter, Space or ArrowDown opens it on the first item;
 * ArrowUp/ArrowDown/Home/End move; Escape closes and returns focus to the button; Tab or a click
 * outside closes it.
 */

export interface MaterialMenuAction {
  id: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  selected?: boolean;
}

export function StudyMaterialActionsMenu({
  title,
  actions,
  triggerRef,
  trigger,
  triggerLabel,
  align = 'right',
}: {
  /** The material title, for the button name "More actions for <title>". */
  title: string;
  actions: MaterialMenuAction[];
  triggerRef: RefObject<HTMLButtonElement>;
  trigger?: ReactNode;
  triggerLabel?: string;
  align?: 'left' | 'right';
}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    items.current[focusIndex]?.focus();
    return undefined;
  }, [focusIndex, open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (actions.length === 0) return null;

  function openAt(index: number) {
    setFocusIndex(index);
    setOpen(true);
  }

  function closeMenu() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAt(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(actions.length - 1);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const last = actions.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: focusIndex === last ? 0 : focusIndex + 1,
      ArrowUp: focusIndex === 0 ? last : focusIndex - 1,
      Home: 0,
      End: last,
    };
    if (event.key in moves) {
      event.preventDefault();
      setFocusIndex(moves[event.key]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrap} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel ?? `More actions for ${title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? setOpen(false) : openAt(0))}
        onKeyDown={onTriggerKeyDown}
        className={`inline-flex h-11 items-center justify-center rounded-full hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky ${trigger ? 'gap-1.5 px-2 text-sm font-semibold text-ink' : 'w-11 text-slate2'}`}
      >
        {trigger ?? <MoreHorizontal className="h-5 w-5" aria-hidden="true" />}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={`Actions for ${title}`}
          onKeyDown={onMenuKeyDown}
          className={`absolute top-12 z-30 max-h-80 overflow-y-auto rounded-2xl border border-hairline bg-canvas-pure p-1.5 shadow-card-soft ${align === 'left' ? 'left-0 w-44' : 'right-0 min-w-[230px]'}`}
        >
          {actions.map((action, index) => (
            <button
              key={action.id}
              ref={(element) => {
                items.current[index] = element;
              }}
              type="button"
              role={action.selected === undefined ? 'menuitem' : 'menuitemradio'}
              aria-checked={action.selected}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => {
                closeMenu();
                action.onSelect();
              }}
              className={`flex min-h-11 w-full items-center rounded-xl px-3 text-left text-sm font-bold hover:bg-surface focus-visible:bg-surface focus-visible:outline-none ${
                action.danger ? 'text-brand-coral' : 'text-ink'
              }`}
            >
              {action.label}
              {action.selected && <Check className="ml-auto h-4 w-4 text-brand-coral" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
